import { auth } from '../firebase';

const BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:3800";

/**
 * Trigger duplicate RCTI detection
 * @returns {Promise<object>} - The result of duplicate detection
 * @throws {Error} - Throws an error if the check fails or the user is not authenticated
 */
export const checkForDuplicates = async () => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }

  try {
    const idToken = await user.getIdToken();
    const url = `${BASE_URL}/duplicate-detection/check`;

    console.log("Triggering duplicate RCTI detection check:", { url });

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({}),
    });

    const responseData = await response.json();

    if (!response.ok) {
      throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
    }

    if (responseData.status !== 'success') {
      throw new Error(responseData.message || 'Failed to check for duplicates');
    }

    return responseData;
  } catch (error) {
    console.error("Error checking for duplicates:", error);
    throw error;
  }
};

/**
 * Trigger duplicate Invoice detection
 * @returns {Promise<object>} - The result of duplicate detection
 * @throws {Error} - Throws an error if the check fails or the user is not authenticated
 */
export const checkForInvoiceDuplicates = async () => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }

  try {
    const idToken = await user.getIdToken();
    const url = `${BASE_URL}/duplicate-detection/check-invoices`;

    console.log("Triggering duplicate Invoice detection check:", { url });

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({}),
    });

    const responseData = await response.json();

    if (!response.ok) {
      throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
    }

    if (responseData.status !== 'success') {
      throw new Error(responseData.message || 'Failed to check for invoice duplicates');
    }

    return responseData;
  } catch (error) {
    console.error("Error checking for invoice duplicates:", error);
    throw error;
  }
};

/**
 * Get list of duplicate pairs (both active and dismissed)
 * @param {string} type - Optional filter by type ('rcti' or 'invoice')
 * @returns {Promise<object>} - The list of duplicates
 * @throws {Error} - Throws an error if the fetch fails or the user is not authenticated
 */
export const listDuplicates = async (type = null) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }

  try {
    const idToken = await user.getIdToken();
    let url = `${BASE_URL}/duplicate-detection/list`;
    if (type) {
      url += `?type=${type}`;
    }

    console.log("Fetching duplicates list:", { url, type });

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
    });

    const responseData = await response.json();

    if (!response.ok) {
      throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
    }

    if (responseData.status !== 'success') {
      throw new Error(responseData.message || 'Failed to list duplicates');
    }

    return responseData;
  } catch (error) {
    console.error("Error listing duplicates:", error);
    throw error;
  }
};

/**
 * Dismiss a duplicate pair
 * @param {string} duplicateId - The ID of the duplicate pair to dismiss
 * @returns {Promise<object>} - The result of the dismiss operation
 * @throws {Error} - Throws an error if the operation fails or the user is not authenticated
 */
export const dismissDuplicate = async (duplicateId) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }

  if (!duplicateId) {
    throw new Error("Duplicate ID is required.");
  }

  try {
    const idToken = await user.getIdToken();
    const url = `${BASE_URL}/duplicate-detection/dismiss`;

    console.log("Dismissing duplicate:", { url, duplicateId });

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ duplicateId }),
    });

    const responseData = await response.json();

    if (!response.ok) {
      throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
    }

    if (responseData.status !== 'success') {
      throw new Error(responseData.message || 'Failed to dismiss duplicate');
    }

    return responseData;
  } catch (error) {
    console.error("Error dismissing duplicate:", error);
    throw error;
  }
};
